/**
 * Kölner Phonetik (Cologne phonetic) encoder for German names.
 * Returns a digit string; equal strings ⇒ names sound alike.
 * Reference algorithm (Postel 1969): context-sensitive per-letter codes,
 * then collapse repeats and drop zeros (except a leading one).
 */
function normalize(input: string): string {
  return input
    .replace(/ß/g, 'SS')
    .toUpperCase()
    .replace(/Ä/g, 'A').replace(/Ö/g, 'O').replace(/Ü/g, 'U')
    .replace(/[^A-Z]/g, '');
}

export function colognePhonetic(input: string): string {
  const s = normalize(input);
  const n = s.length;
  const code: string[] = [];

  const isVowel = (c: string) => 'AEIJOUY'.includes(c);

  for (let i = 0; i < n; i++) {
    const c = s[i];
    const prev = i > 0 ? s[i - 1] : '';
    const next = i < n - 1 ? s[i + 1] : '';
    let d = '';

    if (isVowel(c)) d = '0';
    else if (c === 'H') d = '';
    else if (c === 'B') d = '1';
    else if (c === 'P') d = next === 'H' ? '3' : '1';
    else if (c === 'D' || c === 'T') d = next !== '' && 'CSZ'.includes(next) ? '8' : '2';
    else if (c === 'F' || c === 'V' || c === 'W') d = '3';
    else if (c === 'G' || c === 'K' || c === 'Q') d = '4';
    else if (c === 'C') {
      if (i === 0) d = next !== '' && 'AHKLOQRUX'.includes(next) ? '4' : '8';
      // Non-initial branch: i > 0, so `prev` is always non-empty — no empty-guard needed.
      else d = 'SZ'.includes(prev) ? '8' : (next !== '' && 'AHKOQUX'.includes(next) ? '4' : '8');
    } else if (c === 'X') d = prev !== '' && 'CKQ'.includes(prev) ? '8' : '48';
    else if (c === 'L') d = '5';
    else if (c === 'M' || c === 'N') d = '6';
    else if (c === 'R') d = '7';
    else if (c === 'S' || c === 'Z') d = '8';

    code.push(d);
  }

  // Collapse consecutive identical digits.
  let joined = '';
  let last = '';
  for (const digitGroup of code) {
    for (const ch of digitGroup) {
      if (ch !== last) joined += ch;
      last = ch;
    }
  }
  // Drop all '0' except a leading one.
  const head = joined.slice(0, 1);
  const tail = joined.slice(1).replace(/0/g, '');
  return head + tail;
}

export function phoneticEquals(a: string, b: string): boolean {
  if (!a || !b) return false;
  const codeA = colognePhonetic(a);
  const codeB = colognePhonetic(b);
  // A term with no encodable letters (digits, "-", punctuation) encodes to "".
  // Two empty codes must NOT count as a match — otherwise a numeric query
  // ("13279") or any dash/number token would phonetically "equal" every other
  // digit/dash token, producing false hits in child-match filtering and the
  // "sounds alike" marker.
  return codeA !== '' && codeA === codeB;
}
